import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChoiceCardChromeType } from '../utils/siteBriefChoiceChrome';

type QuestionIconName = 'chevron-down' | 'chevron-left' | 'chevron-right' | 'arrow-right' | 'info';

const ICON_PATHS: Record<QuestionIconName, string[]> = {
  'chevron-down': ['M6 9l6 6 6-6'],
  'chevron-left': ['M15 6l-6 6 6 6'],
  'chevron-right': ['M9 6l6 6-6 6'],
  'arrow-right': ['M5 12h14', 'M13 6l6 6-6 6'],
  info: ['M12 11v7', 'M12 6.2v.1'],
};

export function QuestionIcon({ name, size = 18 }: { name: QuestionIconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={name === 'info' ? 3 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}

type SiteBriefQuestionPanelProps = {
  chrome: ChoiceCardChromeType;
  // The one line shown while the panel is collapsed.
  summary: ReactNode;
  lang?: string;
  children: ReactNode;
};

// The question Evo asks sits above the message box; it folds to one line so the person can reread the chat.
export default function SiteBriefQuestionPanel({ chrome, summary, lang, children }: SiteBriefQuestionPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);

  return (
    <section className={`sb-question-panel ${collapsed ? 'is-collapsed' : ''}`} aria-label={chrome.panel} lang={lang}>
      <button
        ref={toggle}
        type="button"
        className="sb-question-panel__toggle"
        aria-expanded={!collapsed}
        aria-label={collapsed ? chrome.showQuestions : chrome.hideQuestions}
        onClick={() => setCollapsed((current) => !current)}
      >
        <QuestionIcon name="chevron-down" size={20} />
      </button>
      {collapsed && (
        <div className="sb-question-panel__collapsed">
          <p>{summary}</p>
          <button
            type="button"
            className="sb-question-button sb-question-button--primary"
            onClick={() => {
              setCollapsed(false);
              // The Continue button goes away with the collapsed line, so the focus stays on the panel's toggle.
              toggle.current?.focus();
            }}
          >
            {chrome.continueQuestions}
          </button>
        </div>
      )}
      <div className="sb-question-panel__content" hidden={collapsed}>
        {children}
      </div>
    </section>
  );
}
